import type {
  IntegrationPartner,
  IntegrationPartnerEndpoint,
} from '@/lib/supabase';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// Interface for flattened integration endpoint data (for easier consumption)
export interface IntegrationEndpoint {
  endpoint_url: string;
  environment: string;
  action: string;
  description: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
}

interface IntegrationState {
  partner: IntegrationPartner | null;
  endpoints: IntegrationEndpoint[];
  dataFetched: boolean; // Track if integration data has been fetched
  loading: boolean;
  error: string | null;
}

const initialState: IntegrationState = {
  partner: null,
  endpoints: [],
  dataFetched: false,
  loading: false,
  error: null,
};

const integrationSlice = createSlice({
  name: 'integration',
  initialState,
  reducers: {
    // Set integration partner data
    setIntegrationPartner(state, action: PayloadAction<IntegrationPartner>) {
      state.partner = action.payload;
      state.error = null;
    },

    // Set integration endpoints
    setIntegrationEndpoints(
      state,
      action: PayloadAction<IntegrationEndpoint[]>
    ) {
      state.endpoints = action.payload;
      state.error = null;
    },

    // Set integration data as fetched
    setIntegrationDataFetched(state, action: PayloadAction<boolean>) {
      state.dataFetched = action.payload;
    },

    // Set loading state
    setIntegrationLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },

    // Set error state
    setIntegrationError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
      state.loading = false;
    },

    // Clear integration data
    clearIntegrationData(state) {
      state.partner = null;
      state.endpoints = [];
      state.dataFetched = false;
      state.loading = false;
      state.error = null;
    },

    // Set complete integration data
    setIntegrationData(
      state,
      action: PayloadAction<{
        partner: IntegrationPartner;
        endpoints: IntegrationPartnerEndpoint[];
      }>
    ) {
      const { partner, endpoints } = action.payload;

      // Flatten endpoints for easier consumption
      const flattenedEndpoints: IntegrationEndpoint[] = endpoints.map(
        (endpoint) => ({
          endpoint_url: endpoint.endpoint_url,
          environment: endpoint.environment,
          action: endpoint.integration_endpoint?.action || '',
          description: endpoint.integration_endpoint?.description || '',
          method: endpoint.integration_endpoint?.method || 'GET',
        })
      );

      state.partner = partner;
      state.endpoints = flattenedEndpoints;
      state.dataFetched = true;
      state.loading = false;
      state.error = null;
    },
  },
});

export const {
  setIntegrationPartner,
  setIntegrationEndpoints,
  setIntegrationDataFetched,
  setIntegrationLoading,
  setIntegrationError,
  clearIntegrationData,
  setIntegrationData,
} = integrationSlice.actions;

export default integrationSlice.reducer;
